import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Insight } from "@/components/Insight";
import { RhythmGrid, RhythmCell } from "@/components/RhythmGrid";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

// Block 1 — the tick pattern: bar 1 = beats 1, 3, 4 / bar 2 = beats 2, 3
// Two bars of 4. Each beat is one cell. Use a low-pitched tap (G2) as a "clap".
const tickPattern: RhythmCell[] = [
  // Bar 1
  { hand: "B", on: true,  pitches: ["G2"], caption: "tick" },
  { hand: "B", on: false, pitches: [],     caption: "—" },
  { hand: "B", on: true,  pitches: ["G2"], caption: "tick" },
  { hand: "B", on: true,  pitches: ["G2"], caption: "tick" },
  // Bar 2
  { hand: "B", on: false, pitches: [],     caption: "—" },
  { hand: "B", on: true,  pitches: ["G2"], caption: "tick" },
  { hand: "B", on: true,  pitches: ["G2"], caption: "tick" },
  { hand: "B", on: false, pitches: [],     caption: "—" },
];

// Block 2 — KKS pattern (kick-kick-snare). LH plays low note as "kick",
// RH plays a higher note as "snare". KKS repeated twice per bar.
const kksPattern: RhythmCell[] = [
  { hand: "L", on: true, pitches: ["C2"], caption: "K" },
  { hand: "L", on: true, pitches: ["C2"], caption: "K" },
  { hand: "R", on: true, pitches: ["E4"], caption: "S" },
  { hand: "L", on: true, pitches: ["C2"], caption: "K" },
  { hand: "L", on: true, pitches: ["C2"], caption: "K" },
  { hand: "R", on: true, pitches: ["E4"], caption: "S" },
];

// Block 3 — Swaras Sa-Re-Ga-Ma-Pa in C major over two bars.
// Sa Re Ga Ma Pa | Pa Ma Ga Re Sa
const swaraUp: RhythmCell[] = [
  { hand: "R", on: true, pitches: ["C4"], caption: "Sa" },
  { hand: "R", on: true, pitches: ["D4"], caption: "Re" },
  { hand: "R", on: true, pitches: ["E4"], caption: "Ga" },
  { hand: "R", on: true, pitches: ["F4"], caption: "Ma" },
  { hand: "R", on: true, pitches: ["G4"], caption: "Pa" },
  { hand: "R", on: true, pitches: ["G4"], caption: "Pa" },
  { hand: "R", on: true, pitches: ["F4"], caption: "Ma" },
  { hand: "R", on: true, pitches: ["E4"], caption: "Ga" },
  { hand: "R", on: true, pitches: ["D4"], caption: "Re" },
  { hand: "R", on: true, pitches: ["C4"], caption: "Sa" },
];

export default function PianoFoundation4() {
  const { lesson, course, accent } = findLesson("piano-foundation", "4");

  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/piano-foundation/PF4-Rhythm-Blocks-Animated.mp4"
        caption={`A bar splits into 4 beats · each beat splits into 2 eighths — the same time at three resolutions`}
        hint={`≈ 20 sec · build down, layer by layer`}
      />
      <KeyHighlights>
        <Highlight>A <strong>bar</strong> is a vertical line on the page. Inside two bars sit <strong>4 beats each</strong> — eight beats of music in total.</Highlight>
        <Highlight>A <strong>beat</strong> is a time container — fill it with a note, with a rest, or let a previous note sustain through it.</Highlight>
        <Highlight>Three exercises in this lesson, all drawn as rhythm blocks: <strong>clap a tick pattern</strong> (two bars of 4), then <strong>K-K-S percussion</strong> (kick-kick-snare, six beats), then <strong>swaras</strong> — Sa Re Ga Ma Pa (five per bar). Same notation, different bar lengths — the blocks work whatever the count.</Highlight>
        <Highlight>"Kick" can become your left hand. "Snare" can become your right hand. The piano becomes a percussion instrument before it becomes a melodic one.</Highlight>
        <Highlight>End-of-lesson assignment: compose at least <strong>4 bars</strong> of music using your own block pattern + the swaras you've sung — your first composition.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="What a rhythm block is" title="Rhythm first, then notes drop in" accent={accent.hex}>
        <p>
          Rhythm blocks are a visual way of writing rhythm. Two bars of music, four beats per
          bar, drawn as cells. The vertical line between them is the <strong>bar line</strong>.
          A beat is just a time container — <em>"a space"</em>. You
          fill the space with a note, a rest, or a sustain from the previous note.
        </p>

        <KeyIdea>
          The order is rhythm first, melody second. You build the block pattern (where the
          hits land), then drop the notes onto it. The block tells you <em>when</em>; the
          notes tell you <em>what</em>.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="Block 1 · The clap pattern" title="Tick on some beats, rest on the others" accent={accent.hex}>
        <p>
          The first block is exactly what the lesson here demonstrates. Two bars of four beats. In
          bar 1 he ticks beats <strong>1, 3, 4</strong>; in bar 2 he ticks beats{" "}
          <strong>2, 3</strong>; the rest are rests. Then he claps it.
        </p>

        <RhythmGrid
          cells={tickPattern}
          subdivisions={1}
          beats={4}
          bpm={72}
          label="Bar 1: tick · — · tick · tick   |   Bar 2: — · tick · tick · —"
        />

        <Tip>
          Set a slow pulse first ("one… two… three… four…"), then come in with the ticks. The
          clap doesn't sustain like a piano note — that's fine for now. Sustain comes back in
          when we move from clapping to playing the keyboard.
        </Tip>
      </Insight>

      <Insight eyebrow="Block 2 · Two sounds" title="K-K-S — kick, kick, snare" accent={accent.hex}>
        <p>
          One sound on its own is limiting. The trick this introduces is to use{" "}
          <strong>two different sounds</strong> — a low <em>kick</em> (K) and a higher{" "}
          <em>snare</em> (S). On the body, kick is a chest thump and snare is a clap. On the
          piano, kick can be your left hand and snare can be your right hand.
        </p>

        <Pull>
          The kick could be our left hand, very shortly, and the snare could be our
          right hand, very shortly.
        </Pull>

        <RhythmGrid
          cells={kksPattern}
          subdivisions={1}
          beats={6}
          bpm={92}
          label="K · K · S   |   K · K · S"
        />

        <p>
          That single <em>kick-kick-snare</em> pattern is one of the most familiar grooves in
          popular music — once you have it under both hands, you've already got the rhythmic
          backbone of a lot of songs.
        </p>
      </Insight>

      <Insight eyebrow="Block 3 · Swaras for melody" title="Sa Re Ga Ma Pa — the first five scale notes" accent={accent.hex}>
        <p>
          Once the rhythm grid is comfortable, you stop putting tick marks in the cells and
          start putting <strong>note names</strong>. we use the Indian{" "}
          <strong>swaras</strong> — Sa, Re, Ga, Ma, Pa — for the first five notes of any
          scale. In <strong>C major</strong> they're:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>Sa</strong> — C (the root of the scale)</li>
          <li><strong>Re</strong> — D</li>
          <li><strong>Ga</strong> — E</li>
          <li><strong>Ma</strong> — F</li>
          <li><strong>Pa</strong> — G</li>
        </ul>

        <p>
          The exercise: sing them ascending (Sa-Re-Ga-Ma-Pa), then descending (Pa-Ma-Ga-Re-Sa).
          Get the pitches into your body before you ever touch the keyboard. Then, and only
          then, drop them onto the piano.
        </p>

        <RhythmGrid
          cells={swaraUp}
          subdivisions={1}
          beats={5}
          bpm={104}
          label="Sa Re Ga Ma Pa  ·  Pa Ma Ga Re Sa"
        />

        <Tip>
          Swaras work in any scale — they're scale-degree numbers in disguise. In D major, Sa
          starts on D; in G major, Sa is G. Once you can sing Sa-Re-Ga-Ma-Pa, you can sing it
          in any key just by choosing a new starting pitch.
        </Tip>
      </Insight>

      <Insight eyebrow="The assignment" title="Compose four bars · your first piece of music" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Draw the grid.</strong> Four bars of four beats. Decide which beats get a
            tick and which stay empty. Don't sing or play yet.
          </Step>
          <Step>
            <strong>Add the swaras.</strong> Replace each tick with a swara name — pick freely
            from Sa, Re, Ga, Ma, Pa. The order is your call. This is composition.
          </Step>
          <Step>
            <strong>Sing it.</strong> With no instrument. Hold the pulse, sing each swara as
            it lands. Loop the four bars until you can sing them without looking.
          </Step>
          <Step>
            <strong>Then play it.</strong> Sit at the piano. Sa is C (or any other root if you
            prefer). Play the same melody you just sang. The bar lines and the rests are
            already burnt into your body — you'll find your fingers already know the timing.
          </Step>
        </Steps>

        <p className="text-sm text-nsm-dim">
          The order here is non-negotiable — <strong>blocks → sing → play</strong>. Skip the
          singing step and the rhythm gets fragile the moment your fingers are working hard.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-5"
        label="Lesson 5 · Five Finger Drills (Part 1)"
        why="With rhythm and pitch starting to talk to each other, we'll lock in finger control next — five-finger drills with both hands, on a steady pulse."
      />
    </LessonShell>
  );
}
